/**
 * Report Service
 *
 * Handles report management operations with real API integration.
 */

import apiClient from './api';

/**
 * Get list of reports with search, filter, and pagination
 */
export const getReports = async (params = {}) => {
  try {
    const {
      search = '',
      category = '',
      deliveryMethod = '',
      sortBy = 'name',
      sortOrder = 'asc',
      page = 1,
      limit = 10
    } = params;

    const response = await apiClient.get('/reports', {
      params: {
        search,
        category,
        delivery_method: deliveryMethod,
        sort_by: sortBy,
        sort_order: sortOrder,
        page,
        page_size: limit
      }
    });

    const apiData = response.data?.data || response.data;
    const reportList = apiData?.reports || [];
    const reports = reportList.map(report => ({
      id: report.ID,
      name: report.NAME || report.REPORT_NAME,
      description: report.DESCRIPTION || '',
      category: report.CATEGORY || '',
      filePath: report.FILE_PATH || report.BOXI_FILE_PATH || '',
      deliveryMethod: report.DELIVERY_METHOD || 'SIGNED_URL',
      lastUpdated: (report.UPDATED_AT || report.CREATED_AT || '')?.split('T')[0] || '',
      createdBy: report.CREATED_BY || '',
      isActive: report.IS_ACTIVE !== undefined ? report.IS_ACTIVE : true
    }));

    return {
      data: reports,
      pagination: {
        page: apiData?.pagination?.page || page,
        limit: apiData?.pagination?.page_size || limit,
        total: apiData?.pagination?.total || 0,
        totalPages: apiData?.pagination?.total_pages || 1
      }
    };
  } catch (error) {
    console.error('Failed to fetch reports:', error);
    throw error;
  }
};

/**
 * Get report by ID (includes groups and SFTP site associations)
 */
export const getReportById = async (reportId) => {
  try {
    const response = await apiClient.get(`/reports/${reportId}`);
    const apiData = response.data?.data || response.data;

    return {
      id: apiData.ID,
      name: apiData.NAME || apiData.REPORT_NAME,
      description: apiData.DESCRIPTION || '',
      category: apiData.CATEGORY || '',
      filePath: apiData.FILE_PATH || apiData.BOXI_FILE_PATH || '',
      deliveryMethod: apiData.DELIVERY_METHOD || 'SIGNED_URL',
      emailSubject: apiData.EMAIL_SUBJECT || '',
      emailBody: apiData.EMAIL_BODY || '',
      urlLifespanDays: apiData.URL_LIFESPAN_DAYS || null,
      lastUpdated: (apiData.UPDATED_AT || apiData.CREATED_AT || '')?.split('T')[0] || '',
      createdBy: apiData.CREATED_BY || '',
      isActive: apiData.IS_ACTIVE !== undefined ? apiData.IS_ACTIVE : true,
      groups: (apiData.GROUPS || []).map(g => ({
        id: g.ID,
        name: g.NAME
      })),
      emails: (apiData.EMAILS || []).map(e => ({
        id: e.ID,
        emailAddress: e.EMAIL_ADDRESS,
        name: e.NAME
      })),
      sftpSites: (apiData.SFTP_SITES || []).map(s => ({
        id: s.ID,
        name: s.NAME
      })),
      pickupFolders: (apiData.PICKUP_FOLDERS || []).map(f => ({
        id: f.ID,
        name: f.NAME
      }))
    };
  } catch (error) {
    console.error('Failed to fetch report:', error);
    throw error;
  }
};

/**
 * Create new report
 */
export const createReport = async (reportData) => {
  try {
    const payload = {
      report_name: reportData.name,
      description: reportData.description,
      report_category: reportData.category,
      file_path: reportData.filePath,
      delivery_method: reportData.deliveryMethod || 'SIGNED_URL',
      group_ids: reportData.groupIds || [],
      email_ids: reportData.emailIds || [],
      sftp_site_ids: reportData.sftpSiteIds || [],
      pickup_folder_ids: reportData.pickupFolderIds || []
    };

    // Add optional email customization fields if provided
    if (reportData.emailSubject) {
      payload.email_subject = reportData.emailSubject;
    }
    if (reportData.emailBody) {
      payload.email_body = reportData.emailBody;
    }
    if (reportData.urlLifespanDays) {
      payload.url_lifespan_days = parseInt(reportData.urlLifespanDays, 10);
    }

    const config = {};
    if (reportData.teamId) {
      config.headers = { 'X-Active-Team-Id': reportData.teamId };
    }

    const response = await apiClient.post('/reports', payload, config);

    const apiData = response.data?.data || response.data;

    return {
      data: {
        id: apiData.id || apiData.report_id,
        name: apiData.report_name,
        description: apiData.description || '',
        category: apiData.category || '',
        filePath: apiData.file_path || '',
        deliveryMethod: apiData.delivery_method || 'SIGNED_URL',
        createdDate: new Date().toISOString().split('T')[0]
      }
    };
  } catch (error) {
    console.error('Failed to create report:', error);
    const backendMessage = error.response?.data?.error?.message
                        || error.response?.data?.message;
    if (backendMessage) {
      throw new Error(backendMessage);
    }
    throw error;
  }
};

/**
 * Update report
 */
export const updateReport = async (reportId, reportData) => {
  try {
    const payload = {
      report_name: reportData.name,
      description: reportData.description,
      report_category: reportData.category,
      file_path: reportData.filePath,
      delivery_method: reportData.deliveryMethod || 'SIGNED_URL',
      group_ids: reportData.groupIds || [],
      email_ids: reportData.emailIds || [],
      sftp_site_ids: reportData.sftpSiteIds || [],
      pickup_folder_ids: reportData.pickupFolderIds || []
    };

    // Add optional email customization fields if provided
    if (reportData.emailSubject !== undefined) {
      payload.email_subject = reportData.emailSubject;
    }
    if (reportData.emailBody !== undefined) {
      payload.email_body = reportData.emailBody;
    }
    if (reportData.urlLifespanDays) {
      payload.url_lifespan_days = parseInt(reportData.urlLifespanDays, 10);
    }

    await apiClient.put(`/reports/${reportId}`, payload);

    return {
      data: {
        id: reportId,
        name: reportData.name,
        description: reportData.description,
        category: reportData.category,
        deliveryMethod: reportData.deliveryMethod
      }
    };
  } catch (error) {
    console.error('Failed to update report:', error);
    throw error;
  }
};

/**
 * Archive report
 */
export const archiveReport = async (reportId) => {
  try {
    await apiClient.post(`/reports/${reportId}/archive`);
    return { success: true };
  } catch (error) {
    console.error('Failed to archive report:', error);
    throw error;
  }
};

/**
 * Export reports as CSV — triggers a browser download
 */
export const exportReportsCsv = async () => {
  try {
    const response = await apiClient.get('/reports/export/csv', {
      responseType: 'blob'
    });

    // Extract filename from Content-Disposition header if available
    const disposition = response.headers['content-disposition'];
    let filename = 'reports_export.csv';
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=["']?([^"';\n]+)/);
      if (match && match[1]) {
        filename = match[1];
      }
    }

    // Trigger browser download
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export reports CSV:', error);
    throw error;
  }
};

/**
 * Import reports from a CSV file
 * @param {File} file - The CSV file to import
 * @returns {Promise<object>} Import result with created_count and report_names
 */
export const importReportsCsv = async (file) => {
  try {
    const csvText = await file.text();

    // Send as JSON-wrapped payload to stay within CloudFront OAC body signing limits.
    // Raw text/csv bodies larger than ~46KB fail SigV4 signing through OAC.
    const response = await apiClient.post('/reports/import/csv', {
      csv_data: csvText
    });

    return response.data?.data || response.data;
  } catch (error) {
    console.error('Failed to import reports CSV:', error);

    // Re-throw with structured error details for validation errors
    if (error.response?.data) {
      let errorData = error.response.data;

      // For blob responses (if content-type mismatch), parse the blob
      if (errorData instanceof Blob) {
        const text = await errorData.text();
        try { errorData = JSON.parse(text); } catch { /* keep as-is */ }
      }

      const errMsg = errorData?.error?.message || errorData?.message || 'Import failed';
      const errCode = errorData?.error?.code || null;
      const errDetails = errorData?.error?.details || null;

      const structured = new Error(errMsg);
      structured.errorCode = errCode;
      structured.details = errDetails;
      throw structured;
    }

    throw error;
  }
};
